/**
 * ReviewAnalysisPage
 * 
 * Página de análise técnica com IA.
 * Permite revisar e ajustar quantidades sugeridas pelo Gemini.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Package, Loader2, Sparkles,
  TrendingUp, BarChart3,
  Check, RefreshCcw, BrainCircuit, AlertTriangle,
  Plus, Minus, Trash2, Info, ChevronRight,
  Database, PackageX, BarChart
} from 'lucide-react';
import { useRequisition } from '../../../hooks/usePurchases';
import { reviewService } from '../../../services/purchasesApi';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal';
import Modal from '../../../components/ERP/Modal/Modal';
import './ReviewAnalysisPage.css';

// Types
interface RequisitionItem {
  id: number;
  product_id: number | null;
  product_name: string | null;
  requested_qty: number;
  ai_suggested_qty?: number;
  current_stock?: number;
  min_stock?: number;
  avg_monthly_consumption?: number;
  seasonality_trend?: 'up' | 'stable' | 'down';
  ai_analysis_log?: string;
  priority?: 'normal' | 'urgent' | 'critical';
}

interface Requisition {
  id: number;
  title: string;
  priority: 'normal' | 'urgent' | 'critical';
  status: string;
  created_at: string;
  additional_info?: string | null;
  items: RequisitionItem[];
}

interface ConfirmModalState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: (() => void) | null;
  type: 'warning' | 'danger' | 'info';
}

type Priority = 'normal' | 'urgent' | 'critical';

interface PriorityConfig {
  label: string;
  className: string;
}

const ReviewAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast, showToast, hideToast } = useToast();
  
  // Buscar requisição
  const { requisition, loading, error, refetch } = useRequisition(id);
  
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [showAnalysis, setShowAnalysis] = useState<boolean>(false);
  const [currentItems, setCurrentItems] = useState<RequisitionItem[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState<boolean>(false);
  const [openAnalysisDropdown, setOpenAnalysisDropdown] = useState<number | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.ai-reasoning-container')) {
        setOpenAnalysisDropdown(null);
      }
    };

    if (openAnalysisDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openAnalysisDropdown]);

  // Inicializar itens quando a requisição carregar
  useEffect(() => {
    if (requisition && (requisition as Requisition).items) {
      const req = requisition as Requisition;
      
      const items = req.items.map(item => {
        // Normalizar campos e garantir tipos corretos
        const normalizedItem: RequisitionItem = {
          ...item,
          id: item.id,
          product_id: item.product_id,
          product_name: item.product_name || null,
          requested_qty: Number(item.requested_qty) || 0,
          // Manter ai_suggested_qty como está (pode ser null)
          ai_suggested_qty: item.ai_suggested_qty !== null && item.ai_suggested_qty !== undefined
            ? Number(item.ai_suggested_qty)
            : undefined,
          ai_analysis_log: item.ai_analysis_log || undefined,
          current_stock: item.current_stock !== null && item.current_stock !== undefined
            ? Number(item.current_stock)
            : undefined,
          min_stock: item.min_stock !== null && item.min_stock !== undefined
            ? Number(item.min_stock)
            : undefined,
          avg_monthly_consumption: item.avg_monthly_consumption !== null && item.avg_monthly_consumption !== undefined
            ? Number(item.avg_monthly_consumption)
            : undefined,
          seasonality_trend: item.seasonality_trend || undefined,
          priority: item.priority || undefined
        };
        
        return normalizedItem;
      });
      
      setCurrentItems(items);
      
      // Verificar se há sugestões da IA (qualquer item com ai_suggested_qty válido)
      // Mostrar mesmo se for igual ao requested, para indicar que análise foi feita
      const hasSuggestions = items.some(item => {
        const aiSuggested = item.ai_suggested_qty;
        const hasSuggestion = aiSuggested !== null && 
          aiSuggested !== undefined && 
          !isNaN(Number(aiSuggested)) &&
          Number(aiSuggested) >= 0;
        
        return hasSuggestion;
      });
      
      // Se há sugestões, sempre mostrar análise
      if (hasSuggestions) {
        setShowAnalysis(true);
      }
      
      setHasChanges(false);
    }
  }, [requisition]);

  const getPriorityConfig = (priority: Priority): PriorityConfig => {
    const configs: Record<Priority, PriorityConfig> = {
      critical: { label: 'Crítico', className: 'priority-critical' },
      urgent: { label: 'Urgente', className: 'priority-urgent' },
      normal: { label: 'Normal', className: 'priority-normal' }
    };
    return configs[priority] || configs.normal;
  };

  const formatDate = (date: string | null | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const runGeminiAnalysis = async (): Promise<void> => {
    if (!id) return;
    
    setIsAnalyzing(true);
    try {
      const response = await reviewService.analyze(id);
      if (response.success) {
        showToast('Análise concluída com sucesso!', 'success');
        
        // Aguardar um pouco para garantir que o backend atualizou os dados no banco
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Recarregar dados da requisição
        await refetch();
        
        // Forçar mostrar análise após recarregar
        // O useEffect vai atualizar os currentItems automaticamente
        setTimeout(() => {
          setShowAnalysis(true);
        }, 500);
      } else {
        showToast(response.message || 'Erro ao executar análise', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao executar análise:', error);
      showToast(error.response?.data?.message || 'Erro ao executar análise', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const applyAISuggestions = (): void => {
    const suggested = currentItems.map(item => ({
      ...item,
      requested_qty: item.ai_suggested_qty || item.requested_qty
    }));
    setCurrentItems(suggested);
    setHasChanges(true);
    showToast('Sugestões da IA aplicadas!', 'success');
  };

  const handleUpdateQty = (itemId: number, delta: number): void => {
    setCurrentItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return { ...item, requested_qty: Math.max(0, (item.requested_qty || 0) + delta) };
      }
      return item;
    }));
    setHasChanges(true);
  };

  const handleQtyChange = (itemId: number, value: string | number | null | undefined): void => {
    // Garantir que sempre temos um número válido
    let val: number;
    if (typeof value === 'string') {
      val = parseInt(value, 10) || 0;
    } else if (value === null || value === undefined || isNaN(Number(value))) {
      val = 0;
    } else {
      val = Number(value);
    }
    
    // Garantir que o valor seja não-negativo
    val = Math.max(0, Math.floor(val));
    
    setCurrentItems(prev => {
      const itemIndex = prev.findIndex(item => item.id === itemId);
      if (itemIndex === -1) {
        console.warn(`[ReviewAnalysisPage] Item com ID ${itemId} não encontrado`);
        return prev;
      }
      
      const updated = [...prev];
      updated[itemIndex] = {
        ...updated[itemIndex],
        requested_qty: val
      };
      
      return updated;
    });
    setHasChanges(true);
  };

  const handleRemoveItem = (itemId: number): void => {
    setConfirmModal({
      isOpen: true,
      title: 'Remover Item',
      message: 'Deseja remover este item da requisição?',
      type: 'warning',
      onConfirm: () => {
        setCurrentItems(prev => prev.filter(item => item.id !== itemId));
        setHasChanges(true);
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
      }
    });
  };

  const handleFinalizeReview = async (): Promise<void> => {
    if (!id) return;
    
    if (currentItems.length === 0) {
      showToast('Adicione pelo menos um item à requisição', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      // Preparar itens atualizados para enviar ao backend
      const itemsToUpdate = currentItems.map(item => ({
        id: item.id,
        requested_qty: Number(item.requested_qty) || 0
      }));

      // Aprovar revisão técnica e enviar itens atualizados
      const response = await reviewService.approve(id, true, itemsToUpdate);
      
      if (response.success) {
        showToast('Revisão técnica finalizada com sucesso!', 'success');
        setTimeout(() => {
          navigate('/erp/purchases/review-list');
        }, 1500);
      } else {
        showToast(response.message || 'Erro ao finalizar revisão', 'error');
      }
    } catch (error: any) {
      console.error('[ReviewAnalysisPage] Erro ao finalizar revisão:', error);
      console.error('[ReviewAnalysisPage] Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        stack: error.stack
      });
      showToast(error.response?.data?.message || 'Erro ao finalizar revisão', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const restoreOriginalItems = (): void => {
    if (requisition && (requisition as Requisition).items) {
      const req = requisition as Requisition;
      setCurrentItems(req.items.map(item => ({
        ...item,
        requested_qty: item.requested_qty || 0,
        ai_suggested_qty: item.ai_suggested_qty || item.requested_qty || 0
      })));
      setHasChanges(false);
      showToast('Itens originais restaurados', 'info');
    }
  };

  if (loading) {
    return (
      <div className="review-analysis-page">
        <div className="loading">Carregando requisição...</div>
      </div>
    );
  }

  if (error || !requisition) {
    return (
      <div className="review-analysis-page">
        <div className="error-state">
          <div className="error-icon">
            <AlertTriangle size={48} />
          </div>
          <h2>Requisição Não Encontrada</h2>
          <button 
            className="btn-back-to-list"
            onClick={() => navigate('/erp/purchases/review-list')}
          >
            Voltar para Fila
          </button>
        </div>
      </div>
    );
  }

  const req = requisition as Requisition;
  const priorityConfig = getPriorityConfig((req.priority || 'normal') as Priority);
  const hasLowStock = currentItems.some(item => 
    (item.current_stock || 0) < (item.min_stock || 0)
  );

  return (
    <div className="review-analysis-page">
      {/* Header & Controls Toolbar */}
      <div className="page-header-card">
        <div className="header-content">
          <button 
            className="btn-back-header"
            onClick={() => navigate('/erp/purchases/review-list')}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="header-info">
            <div className="header-title-row">
              <h1>#{req.id}</h1>
              <span className={`priority-badge-header ${priorityConfig.className}`}>
                {priorityConfig.label}
              </span>
            </div>
            <p className="header-subtitle">
              <Database size={10} />
              {req.title} • Solicitado em {formatDate(req.created_at)}
            </p>
            {req.additional_info && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem 1rem',
                background: 'rgba(99, 102, 241, 0.1)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                borderRadius: 'var(--border-radius-lg)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-primary)',
                lineHeight: '1.5'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-primary)',
                  fontSize: '0.625rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  <Info size={12} />
                  Informações Adicionais
                </div>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{req.additional_info}</p>
              </div>
            )}
          </div>
        </div>

        <div className="header-actions">
          <div className="header-actions-group">
            {!showAnalysis ? (
              <button
                className="btn-analyze"
                onClick={runGeminiAnalysis}
                disabled={isAnalyzing}
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="spinner" size={16} />
                    Processando Dados de Consumo...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Auditoria Gemini IA
                  </>
                )}
              </button>
            ) : (
              <div className="analysis-actions">
                <button
                  className="btn-apply-ai"
                  onClick={applyAISuggestions}
                >
                  <RefreshCcw size={16} />
                  Otimizar com IA
                </button>
                <button
                  className="btn-hide-analysis"
                  onClick={() => setShowAnalysis(false)}
                >
                  Ocultar Sugestões
                </button>
              </div>
            )}
            <button
              className="btn-summary"
              onClick={() => setIsSummaryModalOpen(true)}
              title="Ver resumo da auditoria"
            >
              <BarChart size={16} />
              Resumo
            </button>
          </div>
        </div>
      </div>

      <div className="content-grid">
        {/* Main Editor Canvas */}
        <div className="main-content">
          <div className="content-header">
            <h3>
              <Package size={16} />
              Workbench de Ajustes ({currentItems.length} Itens)
            </h3>
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
              {hasChanges && (
                <span className="unsaved-changes">Possui Alterações Não Salvas</span>
              )}
              {showAnalysis && (
                <span style={{ 
                  fontSize: '9px', 
                  color: 'var(--color-primary)',
                  fontWeight: 'var(--font-weight-bold)',
                  textTransform: 'uppercase'
                }}>
                  {currentItems.filter(item => {
                    const aiSuggested = item.ai_suggested_qty != null ? Number(item.ai_suggested_qty) : null;
                    return aiSuggested !== null && !isNaN(aiSuggested);
                  }).length} de {currentItems.length} itens com sugestões
                </span>
              )}
            </div>
          </div>

          {currentItems.length === 0 ? (
            <div className="empty-items-state">
              <div className="empty-icon-large">
                <PackageX size={48} />
              </div>
              <div>
                <h4>Requisição Vazia</h4>
                <p>Você removeu todos os itens da auditoria técnica.</p>
              </div>
              <button 
                className="btn-restore"
                onClick={restoreOriginalItems}
              >
                Restaurar Itens Originais
              </button>
            </div>
          ) : (
            <div className="items-list">
              {currentItems.map((item) => {
                // Converter valores para número para comparação correta
                const aiSuggestedRaw = item.ai_suggested_qty;
                const aiSuggested = aiSuggestedRaw != null && aiSuggestedRaw !== undefined
                  ? Number(aiSuggestedRaw)
                  : null;
                const requested = Number(item.requested_qty) || 0;
                
                // Mostrar sugestão se: showAnalysis está true E (existe ai_suggested_qty válido OU existe ai_analysis_log)
                // Isso garante que mesmo se a sugestão for 0 ou igual, ainda mostra que análise foi feita
                const hasSuggestion = showAnalysis && (
                  (aiSuggested !== null && 
                   aiSuggested !== undefined && 
                   !isNaN(aiSuggested) &&
                   aiSuggested >= 0) ||
                  !!item.ai_analysis_log
                );
                
                const currentStock = item.current_stock || 0;
                const minStock = item.min_stock || 0;
                const isLowStock = currentStock <= minStock;
                
                return (
                  <div
                    key={item.id}
                    className={`item-card ${hasSuggestion ? 'has-suggestion' : ''}`}
                  >
                    <div className="item-grid">
                      {/* Item Description */}
                      <div className="item-description">
                        <div className="item-icon-wrapper">
                          <Package size={24} />
                        </div>
                        <div className="item-info">
                          <p className="item-name">{item.product_name || 'Produto'}</p>
                          <div className="item-badges">
                            <span className={`stock-badge ${isLowStock ? 'low-stock' : ''}`}>
                              Estoque: {currentStock}
                            </span>
                            <span className="min-stock-badge">
                              Mínimo: {minStock}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Insights */}
                      <div className="item-insights">
                        <div className="insight-card">
                          <p className="insight-label">Giro Médio</p>
                          <div className="insight-value">
                            <BarChart3 size={16} />
                            <span>{item.avg_monthly_consumption || 0} un</span>
                          </div>
                        </div>
                        <div className="insight-card">
                          <p className="insight-label">Previsão</p>
                          <div className="insight-value">
                            {item.seasonality_trend === 'up' ? (
                              <>
                                <TrendingUp size={16} className="trend-up" />
                                <span className="trend-up">Alta Sazonal</span>
                              </>
                            ) : (
                              <>
                                <Check size={16} className="trend-stable" />
                                <span className="trend-stable">Consumo Regular</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Quantity Editor */}
                      <div className="quantity-editor">
                        <div className="quantity-controls">
                          <div className="quantity-input-group">
                            <button
                              className="qty-btn"
                              onClick={() => handleUpdateQty(item.id, -1)}
                            >
                              <Minus size={16} />
                            </button>
                            <input
                              type="number"
                              value={item.requested_qty || 0}
                              onChange={(e) => handleQtyChange(item.id, e.target.value)}
                              className="qty-input"
                              min="0"
                            />
                            <button
                              className="qty-btn"
                              onClick={() => handleUpdateQty(item.id, 1)}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                          <button
                            className="btn-remove-item"
                            onClick={() => handleRemoveItem(item.id)}
                            title="Remover item da requisição"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>

                        {hasSuggestion && (
                          <div className="ai-suggestion">
                            {aiSuggested !== null && aiSuggested !== undefined && !isNaN(aiSuggested) ? (
                              <>
                                <div className="suggestion-badge">
                                  <p className="suggestion-label">IA Sugere</p>
                                  <p className="suggestion-value">{aiSuggested}</p>
                                </div>
                                {aiSuggested !== null && aiSuggested !== undefined && !isNaN(aiSuggested) && aiSuggested !== requested && aiSuggested > 0 && (
                                  <button
                                    className="btn-accept-suggestion"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleQtyChange(item.id, aiSuggested);
                                    }}
                                    title="Aceitar sugestão da IA"
                                  >
                                    <RefreshCcw size={12} />
                                  </button>
                                )}
                                {aiSuggested === requested && (
                                  <span className="suggestion-same" title="Sugestão igual à quantidade solicitada">
                                    ✓
                                  </span>
                                )}
                              </>
                            ) : (
                              <div className="suggestion-badge" style={{ opacity: 0.7 }}>
                                <p className="suggestion-label">Análise Realizada</p>
                                <p className="suggestion-value" style={{ fontSize: '10px' }}>
                                  Sem sugestão numérica
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* AI Reasoning - Icon with Dropdown */}
                      {showAnalysis && item.ai_analysis_log && (
                        <div className="ai-reasoning-container">
                          <button
                            className="ai-reasoning-icon-btn"
                            onClick={(e) => {
                              const button = e.currentTarget;
                              const rect = button.getBoundingClientRect();
                              const dropdownId = openAnalysisDropdown === item.id ? null : item.id;
                              setOpenAnalysisDropdown(dropdownId);
                              
                              // Se está abrindo, calcular posição
                              if (dropdownId !== null) {
                                setTimeout(() => {
                                  const dropdown = document.querySelector(`[data-dropdown-id="${item.id}"]`) as HTMLElement;
                                  if (dropdown) {
                                    const viewportWidth = window.innerWidth;
                                    const viewportHeight = window.innerHeight;
                                    const dropdownWidth = dropdown.offsetWidth;
                                    const dropdownHeight = dropdown.offsetHeight;
                                    
                                    let left = rect.left;
                                    let top = rect.bottom + 8;
                                    
                                    // Ajustar se ultrapassar a borda direita
                                    if (left + dropdownWidth > viewportWidth - 16) {
                                      left = viewportWidth - dropdownWidth - 16;
                                    }
                                    
                                    // Ajustar se ultrapassar a borda inferior
                                    if (top + dropdownHeight > viewportHeight - 16) {
                                      top = rect.top - dropdownHeight - 8;
                                    }
                                    
                                    // Garantir que não ultrapasse a borda esquerda
                                    if (left < 16) {
                                      left = 16;
                                    }
                                    
                                    // Garantir que não ultrapasse a borda superior
                                    if (top < 16) {
                                      top = 16;
                                    }
                                    
                                    dropdown.style.left = `${left}px`;
                                    dropdown.style.top = `${top}px`;
                                  }
                                }, 0);
                              }
                            }}
                            title="Ver análise da IA"
                            aria-label="Ver análise da IA"
                            aria-expanded={openAnalysisDropdown === item.id}
                          >
                            <BrainCircuit size={18} />
                          </button>
                          {openAnalysisDropdown === item.id && (
                            <div 
                              className="ai-reasoning-dropdown"
                              data-dropdown-id={item.id}
                            >
                              <div className="ai-reasoning-header">
                                <BrainCircuit size={16} />
                                <span>Análise da IA</span>
                                <button
                                  className="ai-reasoning-close"
                                  onClick={() => setOpenAnalysisDropdown(null)}
                                  aria-label="Fechar"
                                >
                                  ×
                                </button>
                              </div>
                              <div className="ai-reasoning-content">
                                <p>{item.ai_analysis_log}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Resumo da Auditoria */}
      <Modal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        title="Resumo da Auditoria"
        size="medium"
      >
        <div className="summary-card-modal">
          <div className="summary-content">
            <div className="summary-item">
              <div>
                <p className="summary-label">Itens Pendentes</p>
                <p className="summary-value">{currentItems.length}</p>
              </div>
              <div className="summary-icon">
                <ChevronRight size={18} />
              </div>
            </div>

            <div className="summary-stats">
              <div className="stat-row">
                <span className="stat-label">Lead Time Médio</span>
                <span className="stat-value">3.5 Dias</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Risco de Ruptura</span>
                <span className={`stat-value ${hasLowStock ? 'critical' : 'low'}`}>
                  {hasLowStock ? 'CRÍTICO' : 'BAIXO'}
                </span>
              </div>
            </div>

            <div className="summary-actions">
              <div className="info-box">
                <Info size={16} />
                <p>
                  A aprovação técnica libera esta requisição para o estágio de cotação com fornecedores homologados.
                </p>
              </div>

              <button
                className="btn-finalize"
                onClick={handleFinalizeReview}
                disabled={currentItems.length === 0 || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="spinner" size={18} />
                    Finalizando...
                  </>
                ) : (
                  <>
                    Finalizar Auditoria
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <button
                className="btn-suspend"
                onClick={() => navigate('/erp/purchases/review-list')}
              >
                Suspender Análise
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' })}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
        }}
        confirmText="Confirmar"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default ReviewAnalysisPage;
